/**
 * Knowledge Graph Core
 *
 * Pure mappers that turn existing Losi entities (Spaces tasks/notes/etc,
 * Nexus contacts/leads/companies/conversations/bookings, memories, chats,
 * pipeline runs) into node upserts + edge specs, plus incremental indexing
 * (indexEntity) and traversal (traverse) for "everything related to X".
 *
 * Primary responsibilities:
 * - buildNodeFrom* pure mappers (entity -> IndexedEntity)
 * - indexEntity: upsert node + resolve/upsert edges idempotently
 * - traverse: BFS across edges to a bounded depth
 *
 * Dependencies:
 * - lib/knowledge-graph/types
 * - db/knowledge-graph (server-safe data layer)
 */

import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from '@/supabase/types'
import {
  deleteNodesForSource,
  getNeighbors,
  getNode,
  getNodeEmbedding,
  matchNodesByEmbedding,
  traverseGraphSql,
  updateNodeEmbedding,
  upsertEdge,
  upsertNode
} from '@/db/knowledge-graph'
import { embedNode, needsEmbedding } from '@/lib/knowledge-graph/embeddings'
import { logger } from '@/lib/logger'
import {
  EdgeSpec,
  EdgeType,
  IndexedEntity,
  KnowledgeGraphEdge,
  KnowledgeGraphNode,
  NodeType,
  NodeUpsert,
  TraverseParams,
  TraverseResult
} from '@/lib/knowledge-graph/types'

// --------------

/**
 * Default relevance weight per edge type. Higher = stronger relationship, used
 * to rank related items so the most meaningful connections surface first
 * instead of an arbitrary alphabetical order. Direct entity links and
 * conversions outrank structural/authorship edges.
 */
const EDGE_WEIGHT: Record<EdgeType, number> = {
  converted_from: 9,
  linked_to: 8,
  references: 7,
  mentions: 6,
  assigned_to: 5,
  belongs_to_sprint: 4,
  belongs_to_folder: 4,
  sourced_from_campaign: 4,
  created_by: 2
}

function weightFor(edgeType: EdgeType): number {
  return EDGE_WEIGHT[edgeType] ?? 1
}

// --------------

/** Truncate a summary to keep node rows compact. */
function clip(text: string | null | undefined, max = 500): string | null {
  if (!text) return null
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed
  return trimmed.slice(0, max - 1) + '…'
}

// --------------
// Pure mappers: Spaces
// --------------

export interface SpaceTaskRecord {
  id: string
  workspace_id: string
  title?: string | null
  name?: string | null
  description?: string | null
  created_by?: string | null
  assignee_id?: string | null
  sprint_id?: string | null
  folder_id?: string | null
  [key: string]: unknown
}

export function buildNodeFromSpaceTask(task: SpaceTaskRecord): IndexedEntity {
  const node: NodeUpsert = {
    workspaceId: task.workspace_id,
    nodeType: 'space_task',
    sourceId: task.id,
    title: task.title ?? task.name ?? null,
    summary: clip(task.description),
    metadata: {
      createdBy: task.created_by ?? null,
      assigneeId: task.assignee_id ?? null,
      sprintId: task.sprint_id ?? null,
      folderId: task.folder_id ?? null
    }
  }

  const edges: EdgeSpec[] = []

  if (task.created_by) {
    edges.push({
      edgeType: 'created_by',
      to: { nodeType: 'user', sourceId: `user:${task.created_by}` },
      weight: weightFor('created_by')
    })
  }
  if (task.assignee_id) {
    edges.push({
      edgeType: 'assigned_to',
      to: { nodeType: 'user', sourceId: `user:${task.assignee_id}` },
      weight: weightFor('assigned_to')
    })
  }
  if (task.sprint_id) {
    edges.push({
      edgeType: 'belongs_to_sprint',
      to: { nodeType: 'space_event', sourceId: task.sprint_id },
      weight: weightFor('belongs_to_sprint')
    })
  }
  if (task.folder_id) {
    edges.push({
      edgeType: 'belongs_to_folder',
      to: { nodeType: 'space_note', sourceId: task.folder_id },
      weight: weightFor('belongs_to_folder')
    })
  }

  return { node, edges }
}

// --------------

export interface SpaceNoteRecord {
  id: string
  workspace_id: string
  title?: string | null
  content?: string | null
  created_by?: string | null
  folder_id?: string | null
  [key: string]: unknown
}

export function buildNodeFromSpaceNote(note: SpaceNoteRecord): IndexedEntity {
  const node: NodeUpsert = {
    workspaceId: note.workspace_id,
    nodeType: 'space_note',
    sourceId: note.id,
    title: note.title ?? null,
    summary: clip(note.content),
    metadata: { createdBy: note.created_by ?? null, folderId: note.folder_id ?? null }
  }

  const edges: EdgeSpec[] = []
  if (note.created_by) {
    edges.push({
      edgeType: 'created_by',
      to: { nodeType: 'user', sourceId: `user:${note.created_by}` },
      weight: weightFor('created_by')
    })
  }
  if (note.folder_id) {
    edges.push({
      edgeType: 'belongs_to_folder',
      to: { nodeType: 'space_note', sourceId: note.folder_id },
      weight: weightFor('belongs_to_folder')
    })
  }
  return { node, edges }
}

// --------------

export interface SpaceFileRecord {
  id: string
  workspace_id: string
  name?: string | null
  description?: string | null
  created_by?: string | null
  folder_id?: string | null
  [key: string]: unknown
}

export function buildNodeFromSpaceFile(file: SpaceFileRecord): IndexedEntity {
  const node: NodeUpsert = {
    workspaceId: file.workspace_id,
    nodeType: 'space_file',
    sourceId: file.id,
    title: file.name ?? null,
    summary: clip(file.description),
    metadata: { createdBy: file.created_by ?? null, folderId: file.folder_id ?? null }
  }

  const edges: EdgeSpec[] = []
  if (file.folder_id) {
    edges.push({
      edgeType: 'belongs_to_folder',
      to: { nodeType: 'space_note', sourceId: file.folder_id },
      weight: weightFor('belongs_to_folder')
    })
  }
  return { node, edges }
}

// --------------

export interface SpaceEventRecord {
  id: string
  workspace_id: string
  title?: string | null
  description?: string | null
  start_time?: string | null
  created_by?: string | null
  task_id?: string | null
  [key: string]: unknown
}

export function buildNodeFromSpaceEvent(event: SpaceEventRecord): IndexedEntity {
  const node: NodeUpsert = {
    workspaceId: event.workspace_id,
    nodeType: 'space_event',
    sourceId: event.id,
    title: event.title ?? null,
    summary: clip(event.description),
    metadata: {
      createdBy: event.created_by ?? null,
      startTime: event.start_time ?? null,
      taskId: event.task_id ?? null
    }
  }

  const edges: EdgeSpec[] = []
  if (event.created_by) {
    edges.push({
      edgeType: 'created_by',
      to: { nodeType: 'user', sourceId: `user:${event.created_by}` },
      weight: weightFor('created_by')
    })
  }
  if (event.task_id) {
    edges.push({
      edgeType: 'linked_to',
      to: { nodeType: 'space_task', sourceId: event.task_id },
      weight: weightFor('linked_to')
    })
  }
  return { node, edges }
}

// --------------
// Pure mappers: Nexus
// --------------

export type NexusRecordKind = 'contact' | 'lead' | 'company' | 'conversation' | 'booking'

export interface NexusRecord {
  id: string
  workspace_id: string
  name?: string | null
  full_name?: string | null
  title?: string | null
  subject?: string | null
  summary?: string | null
  description?: string | null
  company_id?: string | null
  contact_id?: string | null
  conversation_id?: string | null
  campaign_id?: string | null
  converted_from_lead_id?: string | null
  created_by?: string | null
  [key: string]: unknown
}

const NEXUS_KIND_TO_NODE: Record<NexusRecordKind, NodeType> = {
  contact: 'nexus_contact',
  lead: 'nexus_lead',
  company: 'nexus_company',
  conversation: 'nexus_conversation',
  booking: 'nexus_booking'
}

export function buildNodeFromNexusRecord(
  kind: NexusRecordKind,
  record: NexusRecord
): IndexedEntity {
  const node: NodeUpsert = {
    workspaceId: record.workspace_id,
    nodeType: NEXUS_KIND_TO_NODE[kind],
    sourceId: record.id,
    title: record.name ?? record.full_name ?? record.title ?? record.subject ?? null,
    summary: clip(record.summary ?? record.description),
    metadata: {
      companyId: record.company_id ?? null,
      contactId: record.contact_id ?? null,
      conversationId: record.conversation_id ?? null,
      campaignId: record.campaign_id ?? null,
      convertedFromLeadId: record.converted_from_lead_id ?? null,
      createdBy: record.created_by ?? null
    }
  }

  const edges: EdgeSpec[] = []

  if (record.company_id) {
    edges.push({
      edgeType: 'linked_to',
      to: { nodeType: 'nexus_company', sourceId: record.company_id },
      weight: weightFor('linked_to')
    })
  }
  if (record.contact_id) {
    edges.push({
      edgeType: 'linked_to',
      to: { nodeType: 'nexus_contact', sourceId: record.contact_id },
      weight: weightFor('linked_to')
    })
  }
  if (record.conversation_id) {
    edges.push({
      edgeType: 'linked_to',
      to: {
        nodeType: 'nexus_conversation',
        sourceId: record.conversation_id
      },
      weight: weightFor('linked_to')
    })
  }
  if (record.campaign_id) {
    edges.push({
      edgeType: 'sourced_from_campaign',
      to: { nodeType: 'nexus_conversation', sourceId: `campaign:${record.campaign_id}` },
      weight: weightFor('sourced_from_campaign')
    })
  }
  if (record.converted_from_lead_id) {
    edges.push({
      edgeType: 'converted_from',
      to: { nodeType: 'nexus_lead', sourceId: record.converted_from_lead_id },
      weight: weightFor('converted_from')
    })
  }

  return { node, edges }
}

// --------------
// Pure mappers: Memory / Chat / Pipeline
// --------------

export interface MemoryRecord {
  id: string
  workspace_id: string
  title?: string | null
  content?: string | null
  [key: string]: unknown
}

export function buildNodeFromMemory(memory: MemoryRecord): IndexedEntity {
  return {
    node: {
      workspaceId: memory.workspace_id,
      nodeType: 'memory',
      sourceId: memory.id,
      title: memory.title ?? null,
      summary: clip(memory.content)
    },
    edges: []
  }
}

// --------------

export interface ChatRecord {
  id: string
  workspace_id: string
  name?: string | null
  [key: string]: unknown
}

export function buildNodeFromChat(chat: ChatRecord): IndexedEntity {
  return {
    node: {
      workspaceId: chat.workspace_id,
      nodeType: 'chat',
      sourceId: chat.id,
      title: chat.name ?? null,
      summary: null
    },
    edges: []
  }
}

// --------------

export interface PipelineRunRecord {
  id: string
  workspace_id: string
  title?: string | null
  status?: string | null
  [key: string]: unknown
}

export function buildNodeFromPipelineRun(run: PipelineRunRecord): IndexedEntity {
  return {
    node: {
      workspaceId: run.workspace_id,
      nodeType: 'pipeline_run',
      sourceId: run.id,
      title: run.title ?? null,
      summary: run.status ? `Status: ${run.status}` : null,
      metadata: { status: run.status ?? null }
    },
    edges: []
  }
}

// --------------

export type IndexableKind =
  | 'space_task'
  | 'space_note'
  | 'space_file'
  | 'space_event'
  | 'memory'
  | 'chat'
  | 'pipeline_run'
  | NexusRecordKind

/** Loose source record: an entity row with at least id + workspace_id. */
export type EntityRecord = Record<string, unknown>

/** Build an IndexedEntity for any supported kind (dispatch over mappers). */
export function buildIndexedEntity(kind: IndexableKind, record: EntityRecord): IndexedEntity {
  switch (kind) {
    case 'space_task':
      return buildNodeFromSpaceTask(record as unknown as SpaceTaskRecord)
    case 'space_note':
      return buildNodeFromSpaceNote(record as unknown as SpaceNoteRecord)
    case 'space_file':
      return buildNodeFromSpaceFile(record as unknown as SpaceFileRecord)
    case 'space_event':
      return buildNodeFromSpaceEvent(record as unknown as SpaceEventRecord)
    case 'memory':
      return buildNodeFromMemory(record as unknown as MemoryRecord)
    case 'chat':
      return buildNodeFromChat(record as unknown as ChatRecord)
    case 'pipeline_run':
      return buildNodeFromPipelineRun(record as unknown as PipelineRunRecord)
    case 'contact':
    case 'lead':
    case 'company':
    case 'conversation':
    case 'booking':
      return buildNodeFromNexusRecord(kind, record as unknown as NexusRecord)
  }
}

// --------------

/**
 * (Re)embed a node's content when it changed, and persist the vector. Never
 * throws into the caller — embedding is a best-effort enrichment, not part of
 * the write contract. Skips when content is unchanged or embeddings are off.
 */
async function embedNodeIfChanged(
  node: KnowledgeGraphNode,
  supabaseClient?: SupabaseClient<Database>
): Promise<void> {
  try {
    if (!needsEmbedding(node, node.contentHash)) return

    const result = await embedNode({
      nodeType: node.nodeType,
      title: node.title,
      summary: node.summary
    })
    if (!result) return

    await updateNodeEmbedding(node.id, result.embedding, result.contentHash, supabaseClient)
  } catch (error) {
    logger.warn(
      {
        nodeId: node.id,
        nodeType: node.nodeType,
        error: error instanceof Error ? error.message : String(error)
      },
      'Knowledge graph node embedding failed (non-fatal)'
    )
  }
}

// --------------

/**
 * Incrementally + idempotently index an entity: upsert its node, then for each
 * edge spec upsert the target placeholder node and the edge. Targets are
 * upserted so an edge never dangles; when the real target is later indexed its
 * richer fields overwrite the placeholder (same unique key).
 */
export async function indexEntity(
  kind: IndexableKind,
  record: EntityRecord,
  workspaceId: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<KnowledgeGraphNode> {
  const built = buildIndexedEntity(kind, record)

  // Enforce workspace scoping regardless of record contents.
  const node = await upsertNode(
    { ...built.node, workspaceId },
    supabaseClient
  )

  // Semantic layer: (re)embed the node's content when it changed, so the graph
  // is traversable by meaning. Fire-and-forget — embedding must never block or
  // fail an entity write. Skips automatically when content is unchanged or when
  // embeddings are unavailable (no auth).
  void embedNodeIfChanged(node, supabaseClient)

  for (const spec of built.edges) {
    const target = await upsertNode(
      {
        workspaceId,
        nodeType: spec.to.nodeType,
        sourceId: spec.to.sourceId,
        title: spec.to.title ?? null,
        summary: spec.to.summary ?? null,
        metadata: spec.to.metadata ?? {}
      },
      supabaseClient
    )

    await upsertEdge(
      {
        workspaceId,
        fromNodeId: node.id,
        toNodeId: target.id,
        edgeType: spec.edgeType,
        weight: spec.weight,
        metadata: spec.metadata
      },
      supabaseClient
    )
  }

  return node
}

// --------------

/** Remove an entity's node + cascaded edges. */
export async function unindexEntity(
  params: { workspaceId: string; nodeType: NodeType; sourceId: string },
  supabaseClient?: SupabaseClient<Database>
): Promise<void> {
  await deleteNodesForSource(params, supabaseClient)
}

// --------------

/**
 * Traverse the graph from a root node up to `depth`, bidirectionally. Prefers a
 * single recursive-CTE SQL round-trip (traverse_knowledge_graph); falls back to
 * an application-side BFS when the RPC is unavailable. Returns the root, ranked
 * reachable nodes, and traversed edges. With `semantic: true`, fuses in
 * embedding-similar nodes. Bounded + de-duplicated (terminates on cycles).
 */
export async function traverse(
  params: TraverseParams,
  supabaseClient?: SupabaseClient<Database>
): Promise<TraverseResult> {
  const depth = Math.max(0, Math.min(params.depth ?? 2, 6))

  const root = await getNode(
    { workspaceId: params.workspaceId, nodeType: params.rootNode.nodeType, sourceId: params.rootNode.sourceId },
    supabaseClient
  )

  if (!root) return { root: null, nodes: [], edges: [] }

  let nodes: KnowledgeGraphNode[]
  let edges: KnowledgeGraphEdge[]
  const bestWeightByNode = new Map<string, number>()

  // Fast path: one recursive-CTE query. Falls back to BFS on any RPC error
  // (e.g. migration not yet applied) so the surface never hard-fails.
  let usedSql = false
  try {
    const sql = await traverseGraphSql(
      { workspaceId: params.workspaceId, rootNodeId: root.id, depth },
      supabaseClient
    )
    nodes = sql.nodes
    edges = sql.edges
    for (const [nodeId, reach] of Object.entries(sql.reach)) {
      bestWeightByNode.set(nodeId, reach.bestWeight)
    }
    usedSql = true
  } catch (error) {
    logger.warn(
      { rootId: root.id, error: error instanceof Error ? error.message : String(error) },
      'Knowledge graph recursive-CTE traversal unavailable; falling back to BFS'
    )
    const bfs = await traverseBfs(root, depth, supabaseClient)
    nodes = bfs.nodes
    edges = bfs.edges
  }

  // For the BFS path, derive best-weight-per-node from the collected edges.
  if (!usedSql) {
    for (const edge of edges) {
      for (const endpointId of [edge.toNodeId, edge.fromNodeId]) {
        const prev = bestWeightByNode.get(endpointId) ?? 0
        if (edge.weight > prev) bestWeightByNode.set(endpointId, edge.weight)
      }
    }
  }

  const structuralRanked = [...nodes].sort((a, b) => {
    const wa = bestWeightByNode.get(a.id) ?? 0
    const wb = bestWeightByNode.get(b.id) ?? 0
    if (wb !== wa) return wb - wa
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  })

  if (!params.semantic) {
    return { root, nodes: structuralRanked, edges }
  }

  return fuseSemantic({
    root,
    structuralRanked,
    edges,
    bestWeightByNode,
    params,
    supabaseClient
  })
}

// --------------

/**
 * Application-side BFS fallback: expand outgoing + incoming edges level by
 * level, strongest-first, de-duplicating nodes and edges. Used only when the
 * recursive-CTE RPC is unavailable.
 */
async function traverseBfs(
  root: KnowledgeGraphNode,
  depth: number,
  supabaseClient?: SupabaseClient<Database>
): Promise<{ nodes: KnowledgeGraphNode[]; edges: KnowledgeGraphEdge[] }> {
  const visited = new Set<string>([root.id])
  const nodes: KnowledgeGraphNode[] = []
  const edges: KnowledgeGraphEdge[] = []
  const seenEdges = new Set<string>()

  let frontier: KnowledgeGraphNode[] = [root]

  for (let level = 0; level < depth; level++) {
    const nextFrontier: KnowledgeGraphNode[] = []

    for (const current of frontier) {
      const neighbors = await getNeighbors(current.id, {}, supabaseClient)

      // Expand strongest relationships first so higher-signal nodes are
      // discovered (and de-duplicated in) ahead of weak/structural ones.
      const ordered = [...neighbors].sort((a, b) => b.edge.weight - a.edge.weight)

      for (const { node, edge } of ordered) {
        if (!seenEdges.has(edge.id)) {
          seenEdges.add(edge.id)
          edges.push(edge)
        }
        if (!visited.has(node.id)) {
          visited.add(node.id)
          nodes.push(node)
          nextFrontier.push(node)
        }
      }
    }

    if (nextFrontier.length === 0) break
    frontier = nextFrontier
  }

  return { nodes, edges }
}

// --------------

/** Normalize an edge weight (0..~9) to 0..1 so it composes with cosine. */
function normalizeWeight(weight: number): number {
  const MAX = 9
  return Math.max(0, Math.min(1, weight / MAX))
}

async function fuseSemantic(input: {
  root: KnowledgeGraphNode
  structuralRanked: KnowledgeGraphNode[]
  edges: KnowledgeGraphEdge[]
  bestWeightByNode: Map<string, number>
  params: TraverseParams
  supabaseClient?: SupabaseClient<Database>
}): Promise<TraverseResult> {
  const { root, structuralRanked, edges, bestWeightByNode, params, supabaseClient } = input

  let semanticMatches: { node: KnowledgeGraphNode; similarity: number }[] = []
  try {
    const rootEmbedding = await getNodeEmbedding(root.id, supabaseClient)
    if (rootEmbedding) {
      semanticMatches = await matchNodesByEmbedding(
        {
          workspaceId: params.workspaceId,
          queryEmbedding: rootEmbedding,
          matchCount: params.semanticLimit ?? 10,
          similarityThreshold: params.semanticThreshold ?? 0.7,
          excludeNodeId: root.id
        },
        supabaseClient
      )
    }
  } catch (error) {
    logger.warn(
      { rootId: root.id, error: error instanceof Error ? error.message : String(error) },
      'Knowledge graph semantic fusion failed; returning structural results'
    )
  }

  // Combined score per node: structural weight (normalized) + semantic
  // similarity, so a node that is both linked AND similar ranks highest.
  const structuralScore = new Map<string, number>()
  for (const node of structuralRanked) {
    structuralScore.set(node.id, normalizeWeight(bestWeightByNode.get(node.id) ?? 0))
  }
  const semanticScore = new Map<string, number>()
  for (const match of semanticMatches) {
    semanticScore.set(match.node.id, match.similarity)
  }

  const nodeById = new Map<string, KnowledgeGraphNode>()
  for (const node of structuralRanked) nodeById.set(node.id, node)
  for (const match of semanticMatches) {
    if (!nodeById.has(match.node.id)) nodeById.set(match.node.id, match.node)
  }

  const provenance: Record<string, 'structural' | 'semantic' | 'both'> = {}
  for (const id of nodeById.keys()) {
    const s = structuralScore.has(id)
    const m = semanticScore.has(id)
    provenance[id] = s && m ? 'both' : s ? 'structural' : 'semantic'
  }

  const fused = [...nodeById.values()].sort((a, b) => {
    const sa = (structuralScore.get(a.id) ?? 0) + (semanticScore.get(a.id) ?? 0)
    const sb = (structuralScore.get(b.id) ?? 0) + (semanticScore.get(b.id) ?? 0)
    if (sb !== sa) return sb - sa
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  })

  return { root, nodes: fused, edges, provenance }
}
